import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Paper, Grid, Box } from "@material-ui/core";
import { useIntl } from "react-intl";
import View from "components/utils/View";
import SrCurrency from "components/common/SrCurrency";
import useStyles from "./srPagoCardStyles";
import Card from "./Card";
import CardTransactions from "./CardTransactions";
import useCard from "./useCard";
import { cardStatus } from "./constants";
import CancelCardDialog from "./CancelCardDialog";
import {
  SrButton,
  Typography,
  SrContentHeader,
  SrContentWrapper,
} from "../../../common";

const getFundsValue = ({ funds, currency }) => {
  let result = funds || "";

  if (currency) {
    result = <SrCurrency value={result} currency={currency} />;
  }

  return result;
};

const SrPagoCard = () => {
  const intl = useIntl();
  const classes = useStyles();
  const [isNipSending, setIsNipSending] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { card, transactions, cancelCard, sendNIP, error, loading, disabled } =
    useCard();

  useEffect(() => {
    const dispatchError = () => {
      if (error) toast.error(error);
    };

    dispatchError();
  }, [error]);

  const handleSendNIP = async (cardNumber) => {
    setIsNipSending(true);
    const result = await sendNIP(cardNumber);

    if (result?.data?.success) {
      toast.success(intl.formatMessage({ id: "srPagoCard.sendNipSuccess" }));
      setIsNipSending(false);
    } else {
      toast.error(result?.data?.error?.description);
    }
  };

  const handleCancelCard = async () => {
    const result = await cancelCard(card.cardNumber);

    if (result?.data?.success) {
      toast.success(intl.formatMessage({ id: "srPagoCard.cancelCardSuccess" }));
    } else {
      toast.error(result?.data?.error?.description);
    }
    setShowDialog(false);
  };

  const onCardButtonClick = () =>
    window.open("https://shop.senorpago.com/tarjeta", "_blank");

  return (
    <View translateTitleKey="menu.srPagoCard">
      {showDialog && card && (
        <CancelCardDialog
          onSubmit={handleCancelCard}
          handleClose={() => setShowDialog(false)}
          loading={disabled}
          classes={classes}
        />
      )}
      <Grid container flexDirection="column" spacing={2}>
        <SrContentHeader
          title={intl.formatMessage({ id: "menu.srPagoCard" })}
          actions={
            !card &&
            !loading && (
              <SrButton
                color="primary"
                variant="contained"
                onClick={onCardButtonClick}
              >
                {intl.formatMessage({ id: "common.apply" })}
              </SrButton>
            )
          }
        />
        <SrContentWrapper
          isEmpty={!card}
          isLoading={loading}
          emptyMessage={intl.formatMessage({ id: "srPagoCard.noCards" })}
        >
          {card && (
            <Grid container item spacing={2}>
              <Grid item xs={12} lg={4}>
                <Grid container flexDirection="column" spacing={4}>
                  <Grid item xs={12}>
                    <Paper elevation={0}>
                      <Card
                        key={card.timestamp + Math.random()}
                        status={
                          card?.active ? cardStatus.ACTIVE : cardStatus.CANCELED
                        }
                        cardNumber={card.cardNumber}
                        activationDate={card.activationDate}
                        cancelStatus={card.cancelStatus}
                      />
                    </Paper>
                  </Grid>
                  {!card.cancelStatus && (
                    <Grid item xs={12} alignItems="center">
                      <Paper elevation={0}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          padding={7}
                          gridRowGap={25}
                        >
                          <SrButton
                            variant="contained"
                            color="primary"
                            className={classes.cardButton}
                            fullWidth
                            onClick={() => {
                              handleSendNIP(card.cardNumber);
                            }}
                            disabled={isNipSending && disabled}
                          >
                            {intl.formatMessage({
                              id: "srPagoCard.card.sendNip",
                            })}
                          </SrButton>
                          <SrButton
                            color="secondary"
                            className={classes.cardButton}
                            fullWidth
                            variant="contained"
                            onClick={() => {
                              setShowDialog(true);
                            }}
                          >
                            {intl.formatMessage({
                              id: "srPagoCard.card.cancelCard",
                            })}
                          </SrButton>
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} lg={8}>
                <Paper elevation={0}>
                  <Box padding={5}>
                    <Typography variant="h3">
                      {intl.formatMessage({ id: "common.lastMovements" })}
                    </Typography>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Box
                        margin={8}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Typography variant="body1PrimaryColor">
                          {intl.formatMessage({
                            id: "common.availableBalance",
                          })}
                        </Typography>
                        <Typography variant="bigImpact" color="secondary">
                          {getFundsValue(card)}
                        </Typography>
                      </Box>
                      <CardTransactions
                        transactions={transactions}
                        loading={loading}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </SrContentWrapper>
      </Grid>
    </View>
  );
};

export default SrPagoCard;
